import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, ChevronDown } from 'lucide-react';

interface GuestDropdownProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
}

const GuestDropdown = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'Select guests',
  required = false,
}: GuestDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const guestOptions = [
    { value: '1', label: '1 Guest' },
    { value: '2', label: '2 Guests' },
    { value: '3', label: '3 Guests' },
    { value: '4', label: '4 Guests' },
    { value: '5', label: '5 Guests' },
    { value: '6', label: '6 Guests' },
    { value: '7', label: '7 Guests' },
    { value: '8', label: '8 Guests' },
    { value: '9+', label: '9+ Guests' },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  const handleOptionSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const selectedOption = guestOptions.find(option => option.value === value);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={toggleDropdown}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`w-full flex items-center justify-between px-4 py-3 border border-neutral-300 rounded-md bg-white text-left focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all duration-200 min-h-[44px] ${
          disabled
            ? 'opacity-50 cursor-not-allowed bg-neutral-50'
            : 'hover:border-red-300 cursor-pointer'
        } ${isOpen ? 'ring-2 ring-red-500 border-red-500' : ''}`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label="Select number of guests"
        required={required}
      >
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-neutral-500" />
          <span
            className={`text-sm sm:text-base ${
              selectedOption ? 'text-neutral-900' : 'text-neutral-500'
            }`}
          >
            {selectedOption ? selectedOption.label : placeholder}
          </span>
        </div>
        <ChevronDown
          className={`w-5 h-5 text-neutral-500 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="absolute top-full left-0 right-0 mt-2 bg-white border border-neutral-300 rounded-lg shadow-xl z-[99999] overflow-hidden max-h-[300px] guest-dropdown"
            role="listbox"
            aria-label="Guest options"
          >
            {/* Header */}
            <div className="px-4 py-3 bg-gradient-to-r from-red-800 to-red-700 text-white">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span className="text-sm font-semibold">Number of Guests</span>
              </div>
            </div>

            {/* Guest Options */}
            <div className="max-h-64 overflow-y-auto">
              <div className="p-2">
                {guestOptions.map((option, index) => (
                  <motion.button
                    key={option.value}
                    type="button"
                    onClick={() => handleOptionSelect(option.value)}
                    className={`w-full flex items-center justify-between px-3 py-2 sm:px-4 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 min-h-[40px] sm:min-h-[44px] ${
                      value === option.value
                        ? 'bg-red-800 text-white shadow-md'
                        : 'text-neutral-700 hover:bg-red-50 hover:text-red-800 border border-neutral-200'
                    }`}
                    role="option"
                    aria-selected={value === option.value}
                    whileHover={{
                      scale: value === option.value ? 1 : 1.02,
                      transition: { duration: 0.1 },
                    }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      transition: {
                        delay: index * 0.02,
                        duration: 0.2,
                      },
                    }}
                  >
                    <span>{option.label}</span>
                    {value === option.value && (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="ml-auto">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </motion.div>
                    )}
                  </motion.button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-neutral-50 border-t border-neutral-200">
              <div className="flex items-center justify-between text-xs text-neutral-500">
                <span>For groups of 9+, please call us</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GuestDropdown;
